import { 
  collection, 
  query, 
  where, 
  getDocs, 
  addDoc, 
  updateDoc,
  deleteDoc,
  doc,
  orderBy,
  serverTimestamp,
  getFirestore
} from 'firebase/firestore';
import type { Expense, ExpenseFilter } from '../types/expense';
import { normalizeKhaataType } from '../constants/khaataCategories';
import { startOfDay, startOfWeek, startOfMonth, format, subDays } from 'date-fns';  

const RESTAURANT_ID = 'restaurant_001'; // Replace with actual restaurant ID from auth

const formatExpenseForFirestore = (expense: Partial<Expense>) => {
  const { id: _id, ...expenseData } = expense;
  const entryType = normalizeKhaataType(expense.type);
  return {
    ...expenseData,
    type: entryType,
    restaurantId: RESTAURANT_ID,
    amount: Number(expense.amount),
    date: expense.date || new Date().toISOString().split('T')[0],
    categoryLabel: expense.categoryLabel ?? null,
    billUrl: expense.billUrl ?? null,
    supplierId: expense.supplierId ?? null,
    supplierName: expense.supplierName ?? null,
    invoiceNumber: expense.invoiceNumber?.trim() || null,
    dueDate: expense.dueDate || null,
    updatedAt: serverTimestamp()
  };
};

export const getExpenses = async (filter: ExpenseFilter, restId: any): Promise<Expense[]> => {
  const db = getFirestore();

  try {
    const expensesRef = collection(db, 'expenses');
    const constraints = [where('restId', '==', restId), orderBy('date', 'desc')];

    // Get the start date based on the filter and convert it to "YYYY-MM-DD" format
    let startDate: string | null = null;
    const now = new Date();

    switch (filter.period) {
      case 'daily':
        startDate = format(startOfDay(now), 'yyyy-MM-dd');
        break;
      case 'weekly':
        startDate = format(startOfWeek(now), 'yyyy-MM-dd');
        break;
      case 'monthly':
        startDate = format(startOfMonth(now), 'yyyy-MM-dd');
        break;
      case 'yearly':
        // Yearly will be controlled by the explicit year range constraint below
        startDate = null;
        break;
      case 'custom':
        // For custom, we'll apply both boundaries below; avoid adding startDate here
        startDate = null;
        break;
    }

    if (filter.period === 'custom') {
      // Default to last 7 days if dates are not provided
      const effectiveFrom = filter.fromDate || format(subDays(now, 6), 'yyyy-MM-dd');
      const effectiveTo = filter.toDate || format(now, 'yyyy-MM-dd');
      constraints.push(where('date', '>=', effectiveFrom));
      constraints.push(where('date', '<=', effectiveTo));
    } else if (filter.period === 'yearly' && filter.year) {
      const yearStart = `${filter.year}-01-01`;
      const yearEnd = `${filter.year}-12-31`;
      constraints.push(where('date', '>=', yearStart));
      constraints.push(where('date', '<=', yearEnd));
    } else if (startDate) {
      // For daily/weekly/monthly: start boundary only
      constraints.push(where('date', '>=', startDate));
    }

    const q = query(expensesRef, ...constraints);
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        type: normalizeKhaataType(data.type),
        date: data.date,
        amount: Number(data.amount),
        categoryLabel: data.categoryLabel ?? undefined,
        billUrl: data.billUrl ?? undefined,
        supplierId: data.supplierId ?? undefined,
        supplierName: data.supplierName ?? undefined,
        invoiceNumber: data.invoiceNumber ?? undefined,
        dueDate: data.dueDate ?? undefined,
      } as Expense;
    });
  } catch (error) {
    console.error('Error fetching expenses:', error);
    throw new Error('Failed to fetch expenses. Please try again later.');
  }
};


export const addExpense = async (expense: Omit<Expense, 'id'>): Promise<string> => {
  const db = getFirestore();
  try {
    const expensesRef = collection(db, 'expenses');
    const expenseData = formatExpenseForFirestore(expense);
    const docRef = await addDoc(expensesRef, {
      ...expenseData,
      createdAt: serverTimestamp()
    });
    return docRef.id;
  } catch (error) {
    console.error('Error adding expense:', error);
    throw new Error('Failed to add expense. Please try again later.');
  }
};

export const updateExpense = async (id: string, expense: Partial<Expense>): Promise<void> => {
  const db = getFirestore();
  try {
    const expenseRef = doc(db, 'expenses', id);
    const expenseData = formatExpenseForFirestore(expense);
    await updateDoc(expenseRef, expenseData);
  } catch (error) {
    console.error('Error updating expense:', error);
    throw new Error('Failed to update expense. Please try again later.');
  }
};

export const deleteExpense = async (id: string): Promise<void> => {
  const db = getFirestore();
  try {
    const expenseRef = doc(db, 'expenses', id);
    await deleteDoc(expenseRef);
  } catch (error) {
    console.error('Error deleting expense:', error);
    throw new Error('Failed to delete expense. Please try again later.');
  }
};
